import cx from 'classnames';
import moment, {Moment} from 'moment';
import React, { useCallback, useEffect, useRef, useState, useMemo } from 'react';
import ResizeObserver from 'resize-observer-polyfill';
import { timezoneAdaptor } from '../../wui-core/src';
import type { OrNull } from '../../wui-core/src/types/iCore';
import type { CalendarHourBodyProps, CalendarNode, EventObject, EventObjectInput, EventObjectO, EventObjectT } from './iCalendar';
import { compare, greaterThan, separaTime, splitFirst, upDateItemsFrame } from './util/utils';
import Button from '../../wui-button/src';
import { getChildrenText } from '../../wui-core/src';
import Modal from '../../wui-modal/src';

// 日视图中事件最小高度
const minHeight = 15;
// 日视图事件宽度小于28px时不显示内容
const nullWidth = 28;
// 日视图事件不足以显示两个字时不显示内容显示省略号
const elsWidth = 58;

const CalendarHourBody: React.FC<CalendarHourBodyProps> = (props) => {
    const {
        clsPrefix, fieldid, value, timeEvents, showTimeLine, current, layout,
        onTimeEventsClick, customInterval, onCreateEvent, silderModalBody,
        silderModalHeader, onModalOk, isDragEvent, type, locale, isEditEvent,
        onHourBodyScroll,
        // 时区相关 props
        timezone = 'Asia/Shanghai',
        serverTimezone = 'Asia/Shanghai',
        enableTimezone = true,
        convertToServerTimezone
    } = props;

    // 状态管理
    const [divWidth, setDivWidth] = useState(0);
    const [modalVisible, setModalVisible] = useState(false);
    const [targetEventDom, setTargetEventDom] = useState<any>(null);

    // Refs - 存储计算非全天事件渲染百分比时对应的节点
    const currentFirstStartTimeRef = useRef<string>('');
    const firstEventDomRef = useRef<OrNull<HTMLDivElement>>(null);
    const currLineRef = useRef<OrNull<HTMLDivElement>>(null);
    const fullbodydomRef = useRef<OrNull<HTMLDivElement>>(null);
    const widthDivRef = useRef<OrNull<HTMLDivElement>>(null);
    const resizeObserverRef = useRef<OrNull<ResizeObserver>>(null);

    // 拖拽相关的refs
    const offsetTopRef = useRef(0);
    const startYRef = useRef<any>(0);
    const newHeightRef = useRef<any>(null);
    const startEventTimeRef = useRef<any>('');
    const endEventTimeRef = useRef<any>('');
    const startTimeRef = useRef<any>(''); // 鼠标按下时，此时所在的时间位置
    const diffNumRef = useRef<any>(''); // 鼠标按下时，此时的位置距离滚动元素顶部的距离
    const endPositionYRef = useRef<any>(0); // 鼠标最后放手时位置
    const finishFlagRef = useRef(false); // 鼠标松开，拖拽完成标志
    const draggingRef = useRef(false); // 鼠标是否为拖拽状态，而不是点击状态
    const timerRef = useRef<any>(null);
    const sildeDomRef = useRef<any>(null);
    const eventInitObjRef = useRef<any>(null);
    const isResizingRef = useRef(false);
    const initClientYRef = useRef(0);
    const parentNodeRef = useRef<any>(null);
    const initDomHeightRef = useRef(0);
    const initDOmTopRef = useRef(0);
    const targetNodeRef = useRef<any>(null);
    const draggerPlaceRef = useRef(''); // 记录拖拽上面还是下面
    const draggedHRef = useRef(0);

    // ========== P1: 时区转换逻辑 ==========

    // 转换 timeEvents：从 serverTimezone → timezone 显示
    const convertedTimeEvents = useMemo(() => {
        if (!timeEvents || !enableTimezone) return timeEvents;

        return timeEvents.map(event => {
            // String类型的 start/end 视为 serverTimezone
            let startMoment = typeof event.start === 'string'
                ? moment(event.start)
                : moment(event.start);
            let endMoment = typeof event.end === 'string'
                ? moment(event.end)
                : moment(event.end);

            if (enableTimezone) {
                const startConverted = timezoneAdaptor({
                    value: startMoment.format('YYYY-MM-DD HH:mm:ss'),
                    format: 'YYYY-MM-DD HH:mm:ss',
                    timezone,
                    serverTimezone
                });
                const endConverted = timezoneAdaptor({
                    value: endMoment.format('YYYY-MM-DD HH:mm:ss'),
                    format: 'YYYY-MM-DD HH:mm:ss',
                    timezone,
                    serverTimezone
                });

                startMoment = startConverted.value;
                endMoment = endConverted.value;
            }

            return {
                ...event,
                start: startMoment.format('YYYY-MM-DD HH:mm:ss'),
                end: endMoment.format('YYYY-MM-DD HH:mm:ss')
            };
        });
    }, [timeEvents, enableTimezone, timezone, serverTimezone]);

    // 包装 onCreateEvent 回调：将时间转换为 serverTimezone
    const handleCreateEventWithTimezone = useCallback((eventData: any) => {
        if (!onCreateEvent) return;

        let {start, end, ...rest} = eventData;

        // ✅ 回调时转换为 serverTimezone
        if (enableTimezone && convertToServerTimezone) {
            if (start) {
                const startMoment = moment(start);
                start = convertToServerTimezone(startMoment).format('YYYY-MM-DD HH:mm:ss');
            }
            if (end) {
                const endMoment = moment(end);
                end = convertToServerTimezone(endMoment).format('YYYY-MM-DD HH:mm:ss');
            }
        }

        onCreateEvent({ ...rest, start, end });
    }, [onCreateEvent, enableTimezone, convertToServerTimezone]);

    // 判断n是否在root内
    const contains = useCallback((root: any, n: any) => {
        let node: any = n;
        while (node) {
            if (node === root) return true;
            node = node.parentNode
        }
        return false;
    }, []);

    // 点击contextmenu以外的地方将其关闭
    const onDocumentClick = useCallback((event: any) => {
        let target = event.target;
        let element = targetEventDom;
        let contextDom = element;
        if (contextDom && !contains(contextDom, target)) {
            element.style.boxShadow = 'none'
        }
    }, [targetEventDom, contains]);

    const scrollHandle = useCallback((e: any) => {
        onHourBodyScroll && onHourBodyScroll(e)
    }, [onHourBodyScroll]);

    const onCloseSlide = useCallback(() => {
        let dom = sildeDomRef.current as HTMLElement
        if (dom) {
            dom.innerHTML = ''
            dom.style.height = 0 + 'px'
            dom.style.border = 'none'
        }
        setModalVisible(false)
    }, []);

    // 鼠标按下事件
    const handleMouseDown = useCallback((event: any) => {
        if (!isDragEvent) return
        event.preventDefault(); // 阻止默认行为（如选中文本
        draggingRef.current = false
        timerRef.current = setTimeout(() => {
            draggingRef.current = true
        }, 50)
        finishFlagRef.current = false
        offsetTopRef.current = 0
        startYRef.current = 0
        newHeightRef.current = null
        startTimeRef.current = null
        diffNumRef.current = ''
        endPositionYRef.current = 0

        let mouseDownEvent = event || window.event;
        diffNumRef.current = event.target.getBoundingClientRect().top - event.target.parentNode.getBoundingClientRect().top

        startTimeRef.current = event.target.getAttribute('data-time')
        setModalVisible(false)

        if (!startTimeRef.current) {
            finishFlagRef.current = true
            return
        }
        let startTimeArr = startTimeRef.current.split(':')
        startEventTimeRef.current = startTimeArr[0] + ':' + startTimeArr[1]
        // 计算相对于DIV上边界的距离
        startYRef.current = mouseDownEvent.clientY - offsetTopRef.current;
        document.addEventListener('mousemove', handleMouseMove);
    }, [isDragEvent]);

    // 鼠标松开事件
    const handleMouseUp = useCallback((_event: any) => {
        if (!isDragEvent) return
        if (!draggingRef.current) {
            finishFlagRef.current = true
        } else {
            if (!startTimeRef.current) return
            if (newHeightRef.current !== undefined && newHeightRef.current !== null && !isNaN(newHeightRef.current)) {
                let dateNum = value.format('YYYY-MM-DD')
                let start = ''
                let end = ''
                if (startEventTimeRef.current < endEventTimeRef.current) {
                    start = dateNum + ' ' + startEventTimeRef.current
                    end = dateNum + ' ' + endEventTimeRef.current
                } else {
                    start = dateNum + ' ' + endEventTimeRef.current
                    end = dateNum + ' ' + startEventTimeRef.current
                }
                setModalVisible(true)
                handleCreateEventWithTimezone({start, end, currentDate: value})
            }
            startYRef.current = null; // 重置变量
            newHeightRef.current = null;
            finishFlagRef.current = true
        }
        document.removeEventListener('mousemove', handleMouseMove)
        draggingRef.current = false
    }, [isDragEvent, handleCreateEventWithTimezone, value]);

    // 鼠标移动事件
    const handleMouseMove = useCallback((event: any) => {
        if (finishFlagRef.current || !isDragEvent) return
        let customIntervalNum = isNaN(customInterval) ? 1 : customInterval
        let mouseMoveEvent = event || window.event;
        let currentY = mouseMoveEvent.clientY - startYRef.current; // 根据鼠标位置计算新的高度值
        let dom = sildeDomRef.current as HTMLElement
        let dividend = 60
        if (customIntervalNum > 1) {
            dividend = 15
        }
        if (currentY > 0) {
            // dom.style.height = currentY + 'px'; // 设置DIV的新高度
            dom.style.height = currentY + (15 - currentY % 15) + 'px'; // 设置DIV的新高度
            // domH = currentY + (15 - currentY % 15)
            dom.style.width = type === 'week' ? 'calc(100% - 3px)' : 'calc(100% - 85px)'; // 设置DIV的新高度
            dom.style.top = diffNumRef.current + 'px'
            dom.style.border = '1px solid #3b82f6'
            endPositionYRef.current = currentY + diffNumRef.current
            let endsilderDomTop = parseInt(endPositionYRef.current)
            let endEventTimeHour = parseInt((endsilderDomTop / dividend).toString()) < 10 ? '0' + parseInt((endsilderDomTop / dividend).toString()) : parseInt((endsilderDomTop / dividend).toString())
            let endEventTimeHourNum = parseInt((endsilderDomTop / dividend).toString())
            let endEventTimeMinutes = (endsilderDomTop % 60 < 10 ? '0' + endsilderDomTop % 60 : endsilderDomTop % 60)
            let endEventTimeMinutesNum = endsilderDomTop % 60
            // console.log('分钟数………………………………………………………………', endEventTimeMinutes)
            if (customIntervalNum > 1) {
                endEventTimeMinutes = '00'
                endEventTimeHourNum = endEventTimeHourNum + 1
            } else {
                if (endEventTimeMinutesNum >= 0 && endEventTimeMinutesNum < 15) {
                    endEventTimeMinutes = '15'
                } else if (endEventTimeMinutesNum >= 15 && endEventTimeMinutesNum < 30) {
                    endEventTimeMinutes = '30'
                } else if (endEventTimeMinutesNum >= 30 && endEventTimeMinutesNum < 45) {
                    endEventTimeMinutes = '45'
                } else if (endEventTimeMinutesNum >= 45 && endEventTimeMinutesNum < 60) {
                    endEventTimeMinutes = '00'
                    endEventTimeHourNum = endEventTimeHourNum + 1
                }
            }
            endEventTimeHour = endEventTimeHourNum < 10 ? '0' + endEventTimeHourNum : endEventTimeHourNum
            endEventTimeRef.current = endEventTimeHour + ':' + endEventTimeMinutes
            dom.innerText = `${startEventTimeRef.current} - ${endEventTimeRef.current}`
        } else {
            dom.style.height = -currentY + (15 - (-currentY) % 15) + 'px'; // 设置DIV的新高度
            dom.style.width = type === 'week' ? 'calc(100% - 3px)' : 'calc(100% - 85px)'; // 设置DIV的新高度
            dom.style.top = diffNumRef.current - (-currentY + (15 - (-currentY) % 15)) + 'px'
            dom.style.border = '1px solid #3b82f6'
            endPositionYRef.current = diffNumRef.current - (-currentY)

            let endsilderDomTop = parseInt(endPositionYRef.current)
            let endEventTimeHour = parseInt((endsilderDomTop / dividend).toString()) < 10 ? '0' + parseInt((endsilderDomTop / dividend).toString()) : parseInt((endsilderDomTop / dividend).toString())
            let endEventTimeHourNum = parseInt((endsilderDomTop / dividend).toString())
            let endEventTimeMinutes = (endsilderDomTop % 60 < 10 ? '0' + endsilderDomTop % 60 : endsilderDomTop % 60)
            let endEventTimeMinutesNum = endsilderDomTop % 60
            if (customIntervalNum > 1) {
                endEventTimeMinutes = '00'
            } else {
                if (endEventTimeMinutesNum >= 0 && endEventTimeMinutesNum < 15) {
                    endEventTimeMinutes = '00'
                } else if (endEventTimeMinutesNum >= 15 && endEventTimeMinutesNum < 30) {
                    endEventTimeMinutes = '15'
                } else if (endEventTimeMinutesNum >= 30 && endEventTimeMinutesNum < 45) {
                    endEventTimeMinutes = '30'
                } else if (endEventTimeMinutesNum >= 45 && endEventTimeMinutesNum < 60) {
                    endEventTimeMinutes = '45'
                }
            }
            endEventTimeHour = endEventTimeHourNum < 10 ? '0' + endEventTimeHourNum : endEventTimeHourNum
            endEventTimeRef.current = endEventTimeHour + ':' + endEventTimeMinutes
            dom.innerText = `${endEventTimeRef.current} - ${startEventTimeRef.current}`
        }
        if (!isNaN(currentY)) {
            newHeightRef.current = Math.max(0, currentY); // 限制最小高度为50像素
        }
    }, [isDragEvent, customInterval, type]);

    // 滑动鼠标添加滑过区域
    const creatSildeDom = useCallback(() => {
        if (!isDragEvent) return
        fullbodydomRef.current?.addEventListener('mousedown', handleMouseDown)
        document.addEventListener('mouseup', handleMouseUp)
    }, [isDragEvent, handleMouseDown, handleMouseUp]);

    // 弹窗确认
    const onOk = useCallback(() => {
        onModalOk && onModalOk(onCloseSlide)
        if (draggerPlaceRef.current && parentNodeRef.current) {
            parentNodeRef.current.style.border = 'none'
            parentNodeRef.current.style.borderLeft = '3px solid rgb(20, 192, 254)'
            parentNodeRef.current.style.background = 'rgba(20, 192, 254, 5%)'
            parentNodeRef.current = null
        }
    }, [onModalOk, onCloseSlide]);

    // 点击遮罩取消创建
    const onMaskClick = useCallback(() => {
        onCloseSlide()
        if (draggerPlaceRef.current && parentNodeRef.current) {
            parentNodeRef.current.style.height = initDomHeightRef.current + 'px'
            parentNodeRef.current.style.top = initDOmTopRef.current + 'px'
            parentNodeRef.current.style.border = 'none'
            parentNodeRef.current.style.borderLeft = '3px solid rgb(20, 192, 254)'
            parentNodeRef.current.style.background = 'rgba(20, 192, 254, 5%)'
            parentNodeRef.current = null
            draggerPlaceRef.current = ''
        }
    }, [onCloseSlide]);

    // 右上角关闭按钮事件
    const onCancel = useCallback(() => {
        onCloseSlide()
        if (draggerPlaceRef.current && parentNodeRef.current) {
            parentNodeRef.current.style.height = initDomHeightRef.current + 'px'
            parentNodeRef.current.style.top = initDOmTopRef.current + 'px'
            parentNodeRef.current.style.border = 'none'
            parentNodeRef.current.style.borderLeft = '3px solid rgb(20, 192, 254)'
            parentNodeRef.current.style.background = 'rgba(20, 192, 254, 5%)'
            parentNodeRef.current = null
            draggerPlaceRef.current = ''
        }
    }, [onCloseSlide]);

    const itemMove = useCallback((e: any) => {
        if (!isEditEvent) return
        if (isResizingRef.current) {
            if (draggerPlaceRef.current == 'bottom') { // 事件下方触发拖拽改变
                let diffY = e.clientY - initClientYRef.current
                let diffNum = diffY < 0 && diffY > -15 ? 0 : diffY + (15 - diffY % 15)
                parentNodeRef.current.style.height = (initDomHeightRef.current + diffNum < 15 ? 15 : initDomHeightRef.current + diffNum) + 'px' // 以15分钟为基础加或者减拖拽距离，这里为了方便计算拖拽改变的分钟数
                draggedHRef.current = diffY + (15 - diffY % 15)
            } else { // 事件上方触发拖拽改变
                let diffY = e.clientY - initClientYRef.current
                let diffNum = diffY + (15 - diffY % 15)
                parentNodeRef.current.style.height = initDomHeightRef.current - diffNum + 'px' // 以15分钟为基础加或者减拖拽距离，这里为了方便计算拖拽改变的分钟数
                parentNodeRef.current.style.top = (initDomHeightRef.current - diffNum < 15 ? initDOmTopRef.current + initDomHeightRef.current - 15 : initDOmTopRef.current + diffNum) + 'px'
                draggedHRef.current = diffY + (15 - diffY % 15)
            }
        }
    }, [isEditEvent]);

    const itemUp = useCallback((_e: any) => {
        if (!isEditEvent) return
        isResizingRef.current = false
        let endValue = eventInitObjRef.current.end // 下方拖拽时使用
        let endTime = endValue.split(' ')
        let startValue = eventInitObjRef.current.start // 上方拖拽时使用
        let startTime = startValue.split(' ')
        let roundOff = 0
        let remainder = 0
        if (draggerPlaceRef.current == 'bottom') {
            if (draggedHRef.current > 0) {
                roundOff = Math.floor(draggedHRef.current / 60)
                remainder = draggedHRef.current % 60
                if (remainder + parseInt(endTime[1].split(':')[1]) >= 60) {
                    let lastHour = parseInt(endTime[1].split(':')[0]) + roundOff + 1
                    let lastMinutes = (remainder + parseInt(endTime[1].split(':')[1])) % 60
                    let lastValue = endTime[0] + ' ' + lastHour + ":" + (lastMinutes == 0 ? "00" : lastMinutes)
                    setModalVisible(true)
                    handleCreateEventWithTimezone({start: eventInitObjRef.current.start, end: lastValue, currentDate: value, key: eventInitObjRef.current.key, content: eventInitObjRef.current.content, createOrEdit: 'edit'})
                } else {
                    let lastHour = parseInt(endTime[1].split(':')[0]) + roundOff
                    let lastMinutes = (remainder + parseInt(endTime[1].split(':')[1])) % 60
                    let lastValue = endTime[0] + ' ' + lastHour + ":" + (lastMinutes == 0 ? "00" : lastMinutes)
                    setModalVisible(true)
                    handleCreateEventWithTimezone({start: eventInitObjRef.current.start, end: lastValue, currentDate: value, key: eventInitObjRef.current.key, content: eventInitObjRef.current.content, createOrEdit: 'edit'})
                }
            } else {
                if (initDomHeightRef.current + draggedHRef.current < 15) {
                    let lastValue = '0'
                    if (parseInt(startTime[1].split(':')[1]) >= 0 && parseInt(startTime[1].split(':')[1]) < 45) {
                        lastValue = startTime[0] + ' ' + startTime[1].split(':')[0] + ':' + (parseInt(startTime[1].split(':')[1]) + 15)
                    } else {
                        lastValue = startTime[0] + ' ' + (parseInt(startTime[1].split(':')[0]) + 1 < 10 ? '0' + parseInt(startTime[1].split(':')[0]) + 1 : parseInt(startTime[1].split(':')[0]) + 1) + ':' + ((parseInt(startTime[1].split(':')[1]) + 15) % 60 < 10 ? '0' + (parseInt(startTime[1].split(':')[1]) + 15) % 60 : (parseInt(startTime[1].split(':')[1]) + 15) % 60)
                    }
                    setModalVisible(true)
                    handleCreateEventWithTimezone({start: eventInitObjRef.current.start, end: lastValue, currentDate: value, key: eventInitObjRef.current.key, content: eventInitObjRef.current.content, createOrEdit: 'edit'})
                } else {
                    roundOff = Math.floor(-draggedHRef.current / 60)
                    remainder = -draggedHRef.current % 60
                    if (parseInt(endTime[1].split(':')[1]) - remainder <= 0) {
                        let lastHour = 0
                        if (parseInt(endTime[1].split(':')[1]) - remainder == 0) {
                            lastHour = parseInt(endTime[1].split(':')[0]) - roundOff
                        } else {
                            lastHour = parseInt(endTime[1].split(':')[0]) - roundOff - 1
                        }
                        let lastMinutes = 0
                        if (parseInt(endTime[1].split(':')[1]) == 0 && remainder == 0) {
                            lastMinutes = (parseInt(endTime[1].split(':')[1]) - remainder) % 60
                        } else if (parseInt(endTime[1].split(':')[1]) <= 30) {
                            if (parseInt(endTime[1].split(':')[1]) - remainder === 0) {
                                lastMinutes = 0
                            } else {
                                lastMinutes = 60 + (parseInt(endTime[1].split(':')[1]) - remainder) % 60
                            }
                        } else {
                            lastMinutes = parseInt(endTime[1].split(':')[1]) - remainder
                        }
                        let lastValue = endTime[0] + ' ' + lastHour + ":" + (lastMinutes == 0 ? "00" : lastMinutes)
                        setModalVisible(true)
                        handleCreateEventWithTimezone({start: eventInitObjRef.current.start, end: lastValue, currentDate: value, key: eventInitObjRef.current.key, content: eventInitObjRef.current.content, createOrEdit: 'edit'})
                    } else {
                        let lastHour = parseInt(endTime[1].split(':')[0]) - roundOff
                        let lastMinutes = 0
                        if (parseInt(endTime[1].split(':')[1]) == 0 && remainder == 0) {
                            lastMinutes = (parseInt(endTime[1].split(':')[1]) - remainder) % 60
                        } else if (parseInt(endTime[1].split(':')[1]) <= 30) {
                            if (parseInt(endTime[1].split(':')[1]) - remainder === 0) {
                                lastMinutes = 0
                            } else {
                                lastMinutes = 60 + (parseInt(endTime[1].split(':')[1]) - remainder) % 60
                            }
                        } else {
                            lastMinutes = parseInt(endTime[1].split(':')[1]) - remainder
                        }
                        let lastValue = endTime[0] + ' ' + lastHour + ":" + (lastMinutes == 0 ? "00" : lastMinutes)
                        setModalVisible(true)
                        handleCreateEventWithTimezone({start: eventInitObjRef.current.start, end: lastValue, currentDate: value, key: eventInitObjRef.current.key, content: eventInitObjRef.current.content, createOrEdit: 'edit'})
                    }
                }
            }
        } else {
            if (draggedHRef.current > 0) {
                if (initDomHeightRef.current - draggedHRef.current < 15) {
                    let lastValue = '0'
                    if (parseInt(endTime[1].split(':')[1]) > 15) {
                        lastValue = endTime[0] + ' ' + endTime[1].split(':')[0] + ':' + (parseInt(endTime[1].split(':')[1]) - 15)
                    } else {
                        lastValue = endTime[0] + ' ' + (parseInt(endTime[1].split(':')[0]) - 1 < 10 ? '0' + (parseInt(endTime[1].split(':')[0]) - 1) : parseInt(endTime[1].split(':')[0]) - 1) + ':' + (60 - (15 - (parseInt(endTime[1].split(':')[1]))) % 60)
                    }
                    setModalVisible(true)
                    handleCreateEventWithTimezone({start: lastValue, end: eventInitObjRef.current.end, currentDate: value, key: eventInitObjRef.current.key, content: eventInitObjRef.current.content, createOrEdit: 'edit'})
                } else {
                    roundOff = Math.floor(draggedHRef.current / 60)
                    remainder = draggedHRef.current % 60
                    if (remainder + parseInt(startTime[1].split(':')[1]) >= 60) {
                        let lastHour = parseInt(startTime[1].split(':')[0]) + roundOff + 1
                        let lastMinutes = (remainder + parseInt(startTime[1].split(':')[1])) % 60
                        let lastValue = startTime[0] + ' ' + lastHour + ":" + (lastMinutes == 0 ? "00" : lastMinutes)
                        setModalVisible(true)
                        handleCreateEventWithTimezone({start: lastValue, end: eventInitObjRef.current.end, currentDate: value, key: eventInitObjRef.current.key, content: eventInitObjRef.current.content, createOrEdit: 'edit'})
                    } else {
                        let lastHour = parseInt(startTime[1].split(':')[0]) + roundOff
                        let lastMinutes = (remainder + parseInt(startTime[1].split(':')[1])) % 60
                        let lastValue = startTime[0] + ' ' + lastHour + ":" + (lastMinutes == 0 ? "00" : lastMinutes)
                        setModalVisible(true)
                        handleCreateEventWithTimezone({start: lastValue, end: eventInitObjRef.current.end, currentDate: value, key: eventInitObjRef.current.key, content: eventInitObjRef.current.content, createOrEdit: 'edit'})
                    }
                }
            } else {
                roundOff = Math.floor(-draggedHRef.current / 60)
                remainder = -draggedHRef.current % 60
                if (parseInt(startTime[1].split(':')[1]) - remainder <= 0) {
                    let lastHour = 0
                    if (parseInt(startTime[1].split(':')[1]) - remainder == 0) {
                        lastHour = parseInt(startTime[1].split(':')[0]) - roundOff
                    } else {
                        lastHour = parseInt(startTime[1].split(':')[0]) - roundOff - 1
                    }
                    let lastMinutes = 0
                    if (parseInt(startTime[1].split(':')[1]) == 0 && remainder == 0) {
                        lastMinutes = (parseInt(startTime[1].split(':')[1]) - remainder) % 60
                    } else if (parseInt(startTime[1].split(':')[1]) <= 30) {
                        if (parseInt(startTime[1].split(':')[1]) - remainder === 0) {
                            lastMinutes = 0
                        } else {
                            lastMinutes = 60 + (parseInt(startTime[1].split(':')[1]) - remainder) % 60
                        }
                    } else {
                        lastMinutes = parseInt(startTime[1].split(':')[1]) - remainder
                    }
                    let lastValue = startTime[0] + ' ' + lastHour + ":" + (lastMinutes == 0 ? "00" : lastMinutes)
                    setModalVisible(true)
                    handleCreateEventWithTimezone({start: lastValue, end: eventInitObjRef.current.end, currentDate: value, key: eventInitObjRef.current.key, content: eventInitObjRef.current.content, createOrEdit: 'edit'})
                } else {
                    let lastHour = parseInt(startTime[1].split(':')[0]) - roundOff
                    let lastMinutes = 0
                    if (parseInt(startTime[1].split(':')[1]) == 0 && remainder == 0) {
                        lastMinutes = (parseInt(startTime[1].split(':')[1]) - remainder) % 60
                    } else if (parseInt(startTime[1].split(':')[1]) <= 30) {
                        if (parseInt(startTime[1].split(':')[1]) - remainder === 0) {
                            lastMinutes = 0
                        } else {
                            lastMinutes = (60 + (parseInt(startTime[1].split(':')[1]) - remainder)) % 60
                        }
                    } else {
                        lastMinutes = parseInt(startTime[1].split(':')[1]) - remainder
                    }
                    let lastValue = startTime[0] + ' ' + lastHour + ":" + (lastMinutes == 0 ? "00" : lastMinutes)
                    setModalVisible(true)
                    handleCreateEventWithTimezone({start: lastValue, end: eventInitObjRef.current.end, currentDate: value, key: eventInitObjRef.current.key, content: eventInitObjRef.current.content, createOrEdit: 'edit'})
                }
            }
        }
        targetNodeRef.current.classList.remove('wui-calendar-resize-hover')
        targetNodeRef.current = null
        document.removeEventListener('mousemove', itemMove)
        document.removeEventListener('mouseup', itemUp)
    }, [isEditEvent, handleCreateEventWithTimezone, value, itemMove]);

    const itemDown = useCallback((e: any, itemTemp: any, place: any) => {
        if (!isEditEvent) return
        eventInitObjRef.current = itemTemp
        isResizingRef.current = true
        initClientYRef.current = e.clientY
        parentNodeRef.current = e.target.classList.value === `${clsPrefix}-day-events-item-resizer-line-item` ? e.target.parentNode.parentNode : e.target.parentNode
        initDomHeightRef.current = parentNodeRef.current.getBoundingClientRect().height
        initDOmTopRef.current = parseInt(window.getComputedStyle(parentNodeRef.current)?.top)
        targetNodeRef.current = e.target.classList.value === `${clsPrefix}-day-events-item-resizer-line-item` ? e.target.parentNode : e.target
        targetNodeRef.current.classList.add('wui-calendar-resize-hover')
        parentNodeRef.current.style.border = '1px solid #3b82f6'
        parentNodeRef.current.style.background = '#3b82f61a'
        draggerPlaceRef.current = place // 记录拖拽上面还是下面
        document.addEventListener('mousemove', itemMove)
        document.addEventListener('mouseup', itemUp)
    }, [isEditEvent, clsPrefix, itemMove, itemUp]);

    // 通过该算法，获得对应非全天事件的width、left
    const setAlgor = useCallback((timeEvents: EventObjectO[]) => {
        // 根据开始时间将所有非全天事件排序
        timeEvents.sort(function(x, y) {
            let a = separaTime(x.start, x.end).begin.hour + ':' + separaTime(x.start, x.end).begin.minute;
            let b = separaTime(y.start, y.end).begin.hour + ':' + separaTime(y.start, y.end).begin.minute;
            return compare(a, b)
        })
        let nodeArr: CalendarNode['data'][] = [];
        timeEvents.forEach((item, index) => {
            let temp = separaTime(item.start, item.end);
            nodeArr.push([temp.begin, temp.end, index, item.content, [temp.begin.beginTime, temp.end.endTime], item.key, item.className, item.contentDom])
        })
        let domWidth = widthDivRef.current?.offsetWidth || divWidth;
        return upDateItemsFrame(nodeArr, domWidth)
    }, [divWidth]);

    const handleTimeEventsClick = useCallback((e: React.MouseEvent<HTMLElement>, value: EventObjectInput, time: Moment) => {
        if (onTimeEventsClick) {
            onTimeEventsClick(e, value, time)
        }
        let dom = e.target as HTMLElement
        if (dom.classList.contains(`${clsPrefix}-day-events-item`)) {
            dom.style.boxShadow = type === 'week' ? '' : '0 2px 8px 0 rgba(0, 0, 0, .3)'
            setTargetEventDom(dom)
        }
    }, [onTimeEventsClick, clsPrefix, type]);

    // 渲染当前时间线
    const renderCurrentTime = useCallback(() => {
        const currTime = current.format('HH:mm')
        let intervalNum = (24 / customInterval) % 1 == 0 ? customInterval : 1;
        // 当前时间线对应的位置
        // 当customInterval不为1时，之前的计算结果除以4（因为之前一个格子分成四份一份15像素对应15分钟，设置间隔之后一个格子分成customInterval份，一份15像素对应60分钟）
        const top = intervalNum === 1 ? Number(current.format('HH')) * 60 + Number(current.format('mm')) : (Number(current.format('HH')) * 60 + Number(current.format('mm'))) / 4
        // 分别渲染当前时间线，当前时间线边缘点，当前时间
        const line = (<div ref={currLineRef} className={`${clsPrefix}-curr-line`} style={{top: top}}/>)
        const circle = (<div className={`${clsPrefix}-curr-circle`} style={{top: top}}/>)
        const time = (<div className={`${clsPrefix}-curr-time`} style={{top: top - 8}}>{currTime}</div>)
        return (
            <React.Fragment key={currTime}>
                {line}
                {circle}
                {time}
            </React.Fragment>
        )
    }, [current, customInterval, clsPrefix]);

    // 渲染非全天事件
    const renderDayEvents = useCallback((timeEvents: EventObject[]) => {
        const dateValue = moment(value).format('YYYY-MM-DD');
        let intervalNum = (24 / customInterval) % 1 == 0 ? customInterval : 1;
        let dayArr: EventObjectT[] = [];
        // 获取所有非全天事件
        timeEvents?.forEach((item: EventObject) => {
            let startDate = splitFirst(moment(item.start).format('YYYY-MM-DD HH:mm'))[0],
                endDate = splitFirst(moment(item.end).format('YYYY-MM-DD HH:mm'))[0],
                startTime = splitFirst(moment(item.start).format('YYYY-MM-DD HH:mm'))[1],
                endTime = splitFirst(moment(item.end).format('YYYY-MM-DD HH:mm'))[1],
                content = getChildrenText(item.content).join(''),
                key = item.key,
                className = item.className,
                contentDom = item.contentDom
            if (startDate == endDate && dateValue == startDate) {
                // 事件遍历中获取 到 最 早的事件时间 （初始化滚动位置使用）
                if (!currentFirstStartTimeRef.current || greaterThan(currentFirstStartTimeRef.current, startTime)) {
                    currentFirstStartTimeRef.current = startTime
                }
                dayArr.push({date: startDate, start: startTime, end: endTime, content, key, className, contentDom})
            }
        })
        if (dayArr.length !== 0) {
            let dom: JSX.Element[] = [];
            // 通过setAlgor算法计算所有事件width，left
            let temp = setAlgor(dayArr);
            let style = type === 'week' ? {width: '100%'} : layout == 'left' ? {width: 'calc(100% - 85px)'} : {width: 'calc(100% - 78px)'}
            return (
                <div className={`${clsPrefix}-day-events`} style={style}>
                    {
                        temp.map((item: CalendarNode, index: number) => {
                            let tempp = (item: CalendarNode) => {
                                let startPos = intervalNum === 1 ? item.data[0].hour * 60 + item.data[0].minute + 1 : (item.data[0].hour * 60 + item.data[0].minute + 1) / 4;
                                let beginTime = item.data[0].beginTime;
                                let height = intervalNum === 1 ? item.data[1].hour * 60 + item.data[1].minute - (item.data[0].hour * 60 + item.data[0].minute) - 2 : (item.data[1].hour * 60 + item.data[1].minute - (item.data[0].hour * 60 + item.data[0].minute) - 2) / 4;
                                let content;
                                // 设置最小高度minHeight
                                height = height < minHeight ? minHeight : height;
                                if (item.arguments.width / 100 < nullWidth / (widthDivRef.current ? widthDivRef.current.offsetWidth : divWidth)) {
                                    // 小于一定宽度，内部不显示任何文本
                                    content = null
                                } else if (item.arguments.width / 100 < elsWidth / (widthDivRef.current ? widthDivRef.current.offsetWidth : divWidth)) {
                                    // 小于两个字宽度时，显示...
                                    content = '...'
                                    if (type === 'week') {
                                        content = item.arguments.content
                                    }
                                } else {
                                    content = item.arguments.content
                                }
                                // 设置layout不同取值下对应的style样式
                                let style = layout == "left" ? {width: `calc(${item.arguments.width}% - ${type === 'week' ? 6 : 0}px)`, left: `calc(${item.arguments.left}% + ${type === 'week' ? 3 : 75}px)`, top: startPos, height: height, lineHeight: `${height}px`}
                                    : {width: item.arguments.width + '%', right: `calc(${item.arguments.left}% + 68px)`, top: startPos, height: height, lineHeight: `${height}px`};
                                // 点击非全天事件时打印的参数
                                let itemTemp = {start: dateValue + ' ' + item.data[4][0], end: dateValue + ' ' + item.data[4][1], content: item.data[3], key: item.data[5]}
                                if (item.children.length == 0) {
                                    dom.push(
                                        <div key={item.level + startPos} ref={(ref)=> {
                                            if (!firstEventDomRef.current && currentFirstStartTimeRef.current === beginTime) {
                                                firstEventDomRef.current = ref
                                            }
                                        }} className={`${clsPrefix}-day-events-item ${item.data[6] ? item.data[6] : ''}`} onClick={(e) => handleTimeEventsClick(e, itemTemp, moment(dateValue + ' ' + item.data[4][0]))}
                                        style={style}
                                        title={content as string}
                                        >
                                            {isEditEvent && <div onMouseDown={(e) => itemDown(e, itemTemp, 'top')} className={`${clsPrefix}-day-events-item-resizer-top-line`}>
                                                <div className={`${clsPrefix}-day-events-item-resizer-line-item`}></div>
                                                <div className={`${clsPrefix}-day-events-item-resizer-line-item`}></div>
                                            </div>}
                                            {item.data[7] ? item.data[7] : content}
                                            {isEditEvent && <div onMouseDown={(e) => itemDown(e, itemTemp, 'bottom')} className={`${clsPrefix}-day-events-item-resizer-bottom-line`}>
                                                <div className={`${clsPrefix}-day-events-item-resizer-line-item`}></div>
                                                <div className={`${clsPrefix}-day-events-item-resizer-line-item`}></div>
                                            </div>}
                                        </div>
                                    )
                                } else {
                                    item.children.forEach((i: CalendarNode) => tempp(i))
                                    dom.push(
                                        <div key={item.level + startPos} className={`${clsPrefix}-day-events-item ${item.data[6] ? item.data[6] : ''}`} ref={(ref)=> {
                                            if (!firstEventDomRef.current && currentFirstStartTimeRef.current === beginTime) {
                                                firstEventDomRef.current = ref
                                            }
                                        }} onClick={(e) => handleTimeEventsClick(e, itemTemp, moment(dateValue + ' ' + item.data[4][0]))}
                                        style={style}
                                        title={content as string}
                                        >
                                            {isEditEvent && <div onMouseDown={(e) => itemDown(e, itemTemp, 'top')} className={`${clsPrefix}-day-events-item-resizer-top-line`}>
                                                <div className={`${clsPrefix}-day-events-item-resizer-line-item`}></div>
                                                <div className={`${clsPrefix}-day-events-item-resizer-line-item`}></div>
                                            </div>}
                                            {item.data[7] ? item.data[7] : content}
                                            {isEditEvent && <div onMouseDown={(e) => itemDown(e, itemTemp, 'bottom')} className={`${clsPrefix}-day-events-item-resizer-bottom-line`}>
                                                <div className={`${clsPrefix}-day-events-item-resizer-line-item`}></div>
                                                <div className={`${clsPrefix}-day-events-item-resizer-line-item`}></div>
                                            </div>}
                                        </div>
                                    )
                                }
                            }
                            tempp(item);
                            if (index == temp.length - 1) return dom
                        })
                    }
                </div>
            )
        }
    }, [value, customInterval, type, layout, clsPrefix, divWidth, isEditEvent, setAlgor, handleTimeEventsClick, itemDown]);

    // 渲染body内部，每一份代表一小时，每小时又均分为15分钟
    const renderFullBody = useCallback(() => {
        const momentValue = moment(value);
        let interVal = (24 / customInterval) % 1 == 0 ? 24 / customInterval : 24;
        let intervalNum = (24 / customInterval) % 1 == 0 ? customInterval : 1;
        return (
            <div ref={fullbodydomRef} className={`${clsPrefix}-full-body`}>
                <div ref={(ref) => widthDivRef.current = ref ? ref : widthDivRef.current} className={`${clsPrefix}-full-body-container`}>
                    {momentValue.format('YYYY-MM-DD') == moment().format('YYYY-MM-DD') && showTimeLine !== false && type != 'week' ? renderCurrentTime() : null}
                    <ul>
                        {[...Array(interVal).keys()].map(item => {
                            // 每小时分为4份，一份15分钟
                            let time = item < 10 ? "0" + item : String(item);
                            if (intervalNum === 1) {
                                return [":00", ":15", ":30", ":45"].map((it: string) => {
                                    // 点击每一块区域打印的参数，包含这块区域开始的事件
                                    let outPutValue = {start: momentValue.format('YYYY-MM-DD') + ' ' + (time + it)};
                                    return (
                                        <li key={item + it} data-time={`${time}:${it.slice(1)}`} fieldid={fieldid ? `${fieldid}_bodyTime_${time}_${it.slice(1)}` : undefined} className={cx({[`${clsPrefix}-last-item`]: it == ':45'})} onClick={(e) => handleTimeEventsClick(e, outPutValue, moment(momentValue.format('YYYY-MM-DD') + ' ' + outPutValue.start))}></li>
                                    )
                                })
                            } else {
                                return [...Array(intervalNum).keys()].map((it: number, _index: number) => {
                                    // 点击每一块区域打印的参数，包含这块区域开始的事件
                                    let hour = item * intervalNum + it < 10 ? '0' + (item * intervalNum + it) : item * intervalNum + it
                                    let outPutValue = {start: momentValue.format('YYYY-MM-DD') + ' ' + hour + ':00'};
                                    return (
                                        <li key={item + it} data-time={`${hour}:00`} fieldid={fieldid ? `${fieldid}_bodyTime_${time}_${it}` : undefined} className={cx({[`${clsPrefix}-last-item`]: it + 1 == intervalNum})} onClick={(e) => handleTimeEventsClick(e, outPutValue, moment(momentValue.format('YYYY-MM-DD') + ' ' + outPutValue.start))}></li>
                                    )
                                })
                            }
                        })}
                        {convertedTimeEvents ? renderDayEvents(convertedTimeEvents) : null}
                    </ul>
                    <div className="wui-calendar-slide-dom" ref={sildeDomRef}></div>
                </div>
                <Modal visible={modalVisible} mask onMaskClick={onMaskClick} onCancel={onCancel}>
                    <Modal.Header closeButton>
                        {silderModalHeader ? silderModalHeader : locale?.modalHeader}
                    </Modal.Header>
                    <Modal.Body>
                        <div tinper-next-role="container">
                            {silderModalBody ? silderModalBody : locale?.modalBody}
                        </div>
                    </Modal.Body>
                    <Modal.Footer>
                        <Button onClick={onMaskClick} colors="secondary" style={{ marginRight: 8 }}>{locale?.cancel}</Button>
                        <Button onClick={onOk} colors='primary'>{locale?.ok}</Button>
                    </Modal.Footer>
                </Modal>
            </div>
        )
    }, [value, customInterval, clsPrefix, showTimeLine, type, fieldid, convertedTimeEvents, modalVisible, silderModalBody, silderModalHeader, locale, renderCurrentTime, renderDayEvents, handleTimeEventsClick, onMaskClick, onCancel, onOk]);

    // componentDidMount 转换为 useEffect
    useEffect(() => {
        setDivWidth(widthDivRef.current ? widthDivRef.current.offsetWidth : divWidth);
        if (widthDivRef.current) {
            resizeObserverRef.current = new ResizeObserver(() => {
                setDivWidth(widthDivRef.current ? widthDivRef.current.offsetWidth : divWidth);
            });
            resizeObserverRef.current?.observe(widthDivRef.current);
        }
        // 存在 主线程中 某父级组件未挂载完 导致firstEventDom.offsetTop为0，故添加到时间队列中
        setTimeout(() => {
            // 初始化滚动位置
            if (firstEventDomRef.current && fullbodydomRef.current) {
                fullbodydomRef.current.parentElement!.scrollTop = Math.max(firstEventDomRef.current.offsetTop - 10, 0);
            } else if (currLineRef.current && fullbodydomRef.current) {
                fullbodydomRef.current.parentElement!.scrollTop = Math.max(currLineRef.current.offsetTop - 10, 0);
            }
        }, 0);
        creatSildeDom();
        fullbodydomRef.current?.addEventListener('click', onDocumentClick); // 点击事件是添加阴影，标注选择的事件
        fullbodydomRef.current?.parentNode?.addEventListener('scroll', scrollHandle);

        // componentWillUnmount
        return () => {
            if (resizeObserverRef.current) {
                resizeObserverRef.current.disconnect();
            }
            fullbodydomRef.current?.removeEventListener('mousedown', handleMouseDown);
            document.removeEventListener('mouseup', handleMouseUp);
            document.removeEventListener('mousemove', handleMouseMove);
            fullbodydomRef.current?.removeEventListener('click', onDocumentClick);
            fullbodydomRef.current?.parentNode?.removeEventListener('scroll', scrollHandle);
            clearInterval(timerRef.current);
        };
    }, [onDocumentClick, scrollHandle]);

    // componentDidUpdate 监听widthDiv变化
    useEffect(() => {
        if (widthDivRef.current?.offsetWidth !== divWidth) {
            setDivWidth(widthDivRef.current ? widthDivRef.current.offsetWidth : divWidth);
        }
    });

    // componentDidUpdate 监听value变化 - 切换日期后重置滚动位置
    useEffect(() => {
        // 初始化滚动位置
        if (firstEventDomRef.current && fullbodydomRef.current) {
            fullbodydomRef.current.parentElement!.scrollTop = Math.max(firstEventDomRef.current.offsetTop - 10, 0);
        } else if (currLineRef.current && fullbodydomRef.current) {
            fullbodydomRef.current.parentElement!.scrollTop = Math.max(currLineRef.current.offsetTop - 10, 0);
        }
        firstEventDomRef.current = null;
        currLineRef.current = null;
        currentFirstStartTimeRef.current = '';
    }, [value?.format?.('yyyy-MM-DD')]); // 仅在日期变化时触发

    return renderFullBody();
}

export default CalendarHourBody;